/**
 * Announcement bar(s) shown at the top of the Home page. Fetches the
 * announcements the current user should see (global + their dealer group,
 * within schedule) and stacks them. Dismissible bars are remembered per-browser
 * in localStorage so they stay hidden once closed.
 */
import React, { useEffect, useState } from "react"

import { THEME } from "../config/theme"
import { Announcement, fetchActiveAnnouncements } from "../api/announcements"

const DISMISSED_KEY = "invi_dismissed_announcements"

// Level → bar background. Strong fills with white text read as a banner.
const LEVEL_BG: Record<string, string> = {
  info: THEME.colors.navy,
  warning: THEME.colors.semantic.warning,
  success: THEME.colors.semantic.success,
}

function readDismissed(): number[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    return raw ? (JSON.parse(raw) as number[]) : []
  } catch {
    return []
  }
}

function AnnouncementBar() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [dismissed, setDismissed] = useState<number[]>(readDismissed)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await fetchActiveAnnouncements()
        if (!cancelled) setAnnouncements(data)
      } catch {
        // A failed announcements fetch must never break the dashboard.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const dismiss = (id: number) => {
    const next = [...dismissed, id]
    setDismissed(next)
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next))
    } catch {
      // ignore storage failures
    }
  }

  const visible = announcements.filter((a) => !dismissed.includes(a.id))
  if (visible.length === 0) return null

  return (
    <div style={{ marginBottom: "16px" }}>
      {visible.map((a) => (
        <div
          key={a.id}
          style={{
            background: LEVEL_BG[a.level] ?? THEME.colors.navy,
            color: THEME.colors.white,
            borderRadius: THEME.borderRadius.md,
            padding: "12px 16px",
            marginBottom: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            fontSize: "14px",
            fontFamily: THEME.fonts.primary,
          }}
        >
          <span>{a.message}</span>
          {a.dismissible && (
            <button
              type="button"
              onClick={() => dismiss(a.id)}
              aria-label="Dismiss announcement"
              style={{
                background: "none",
                border: "none",
                color: THEME.colors.white,
                cursor: "pointer",
                fontSize: "16px",
                lineHeight: 1,
                opacity: 0.85,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          )}
        </div>
      ))}
    </div>
  )
}

export default AnnouncementBar
